import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, Renderer2, TemplateRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbDropdownModule, NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NotEmptyPipe, ResidenceInformation } from '@zero/onboarding/domain';
import { ButtonComponent, IconComponent, InputComponent, Item, ModalComponent } from '@zero/shared/ui-web-design-system';
import { GooglePlacesResult, GooglePlacesService } from '@zero/shared/util-geolocation';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  map,
  of,
  switchMap,
} from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [
    OnboardingContainerComponent,
    TranslocoPipe,
    AsyncPipe,
    InputComponent,
    NgbDropdownModule,
    ModalComponent,
    ButtonComponent,
    IconComponent,
    NgbTooltipModule,
    NotEmptyPipe,
  ],
  templateUrl: './residence.component.html',
})
export class ResidenceComponent extends BaseComponent implements OnInit {
  readonly #googlePlacesService = inject(GooglePlacesService);
  readonly #renderer = inject(Renderer2);
  readonly #modalService = inject(NgbModal);
  readonly #destroyRef = inject(DestroyRef);

  readonly #residence = new BehaviorSubject<string>(undefined);
  readonly #searchTerm = new BehaviorSubject<string>(undefined);
  readonly #street = new BehaviorSubject<string>(undefined);
  readonly #streetNumber = new BehaviorSubject<string>(undefined);
  readonly #addition = new BehaviorSubject<string>(undefined);
  readonly #zip = new BehaviorSubject<string>(undefined);
  readonly #city = new BehaviorSubject<string>(undefined);

  public readonly residence$ = this.#residence.asObservable();
  public readonly searchTerm$ = this.#searchTerm.asObservable();
  public readonly street$ = this.#street.asObservable();
  public readonly streetNumber$ = this.#streetNumber.asObservable();
  public readonly addition$ = this.#addition.asObservable();
  public readonly zip$ = this.#zip.asObservable();
  public readonly city$ = this.#city.asObservable();

  public readonly addressSuggestions$ = this.searchTerm$.pipe(
    filter((value) => value && value.trim() !== ''),
    debounceTime(200),
    distinctUntilChanged(),
    switchMap((value) => from(this.#googlePlacesService.getResultsByQuery(`${value} Deutschland`)).pipe(catchError(() => of([])))),
    filter((results) => results.length > 0 && results.every((result) => !!result.street || !!result.city)),
  );
  public readonly isFormValid$ = combineLatest([
    this.street$,
    this.streetNumber$,
    this.zip$,
    this.city$,
    this.residence$,
    this.store.sendingResidenceInformation$,
  ]).pipe(
    map(([street, streetNumber, zip, city, residence, sendingResidenceInformation]) => {
      const notEmpty = new NotEmptyPipe();
      return (
        !!notEmpty.transform(street) &&
        !!notEmpty.transform(streetNumber) &&
        !!notEmpty.transform(zip) &&
        !!notEmpty.transform(city) &&
        !!notEmpty.transform(residence) &&
        !sendingResidenceInformation
      );
    }),
  );

  public displayInvalidResidenceScreen = false;

  override ngOnInit(): void {
    super.ngOnInit();
    this.#googlePlacesService.init(this.#renderer);

    this.#setResidence('DE');
  }

  public copy() {
    const cbd = this.customerStateFacade.customerBaseData;
    this.#street.next(cbd.street1);
    this.#addition.next(cbd.additionalAddress);
    this.#streetNumber.next(cbd.streetNr);
    this.#zip.next(cbd.postalCode);
    this.#city.next(cbd.city);

    this.#searchTerm.next(
      this.#transformToReadableAddress({
        country: this.#residence.value ?? '',
        city: this.#city.value ?? '',
        street: this.#street.value ?? '',
        addition: this.#addition.value ?? '',
        streetNumber: this.#streetNumber.value ?? '',
        zip: this.#zip.value ?? '',
      }),
    );
  }

  protected override async submit(): Promise<void> {
    const isFormValid = await firstValueFrom(this.isFormValid$);
    if (!isFormValid) {
      return;
    }
    this.#modalService.dismissAll();

    this.store.sendResidenceInformation({
      country: this.#residence.value,
      city: this.#city.value,
      street: this.#street.value,
      streetNumber: this.#streetNumber.value,
      addition: this.#addition.value,
      zip: this.#zip.value,
    });
  }

  protected override populateFromData(): void {
    this.store.selectedChildOnboarding$
      .pipe(
        filter((data) => !!data),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(async (data) => {
        if (data) {
          const { strasse, hausnummer, plz, ort, adresszusatz, land } = data;
          this.#street.next(strasse);
          this.#addition.next(adresszusatz);
          this.#streetNumber.next(hausnummer);
          this.#zip.next(plz);
          this.#city.next(ort);
          this.#setResidence(land ?? 'DE');

          this.#searchTerm.next(
            this.#transformToReadableAddress({
              country: this.#residence.value ?? '',
              city: this.#city.value ?? '',
              street: this.#street.value ?? '',
              addition: this.#addition.value ?? '',
              streetNumber: this.#streetNumber.value ?? '',
              zip: this.#zip.value ?? '',
            }),
          );
        }
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.store.sentResidenceInformationSuccessfully$
      .pipe(
        filter((sent) => sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.service.goToNextStep();
      });
  }

  public selectResidence = (item: Item) => this.#residence.next(item.value?.toString());
  public handleSearchTermChange = (term: string) => {
    this.#street.next(undefined);
    this.#streetNumber.next(undefined);
    this.#zip.next(undefined);
    this.#city.next(undefined);
    this.#addition.next(undefined);
    this.#searchTerm.next(term);
  };
  public handleStreetChange = (street: string) => this.#street.next(street);
  public handleStreetNumberChange = (streetNumber: string) => this.#streetNumber.next(streetNumber);
  public handleAdditionChange = (addition: string) => this.#addition.next(addition);
  public handleZipChange = (zip: string) => this.#zip.next(zip);
  public handleCityChange = (city: string) => this.#city.next(city);

  public useAdress(address: GooglePlacesResult, modalContent: TemplateRef<HTMLElement>) {
    this.handleSearchTermChange(this.#transformToReadableAddress(address));
    address?.street && this.handleStreetChange(address.street);
    address?.streetNumber && this.handleStreetNumberChange(address.streetNumber);
    address?.zip && this.handleZipChange(address.zip);
    address?.city && this.handleCityChange(address.city);
    this.#modalService.open(modalContent, { centered: true, size: 'md' });
  }

  public dismissModal(modal: { dismiss: () => void }) {
    this.#searchTerm.next(
      this.#transformToReadableAddress({
        country: this.#residence.value ?? '',
        city: this.#city.value ?? '',
        street: this.#street.value ?? '',
        streetNumber: this.#streetNumber.value ?? '',
        zip: this.#zip.value ?? '',
      }),
    );
    modal.dismiss();
  }

  #transformToReadableAddress(address: GooglePlacesResult | ResidenceInformation) {
    if (!address) {
      return this.#searchTerm.value;
    }

    let readableAddress = '';
    const { street, streetNumber, zip, city } = address;
    if (street) {
      readableAddress += street;
    }
    if (streetNumber) {
      readableAddress += `${readableAddress ? ' ' : ''}${streetNumber}`;
    }
    if (zip || city) {
      readableAddress += `${readableAddress ? ', ' : ''}`;
    }
    if (zip) {
      readableAddress += `${zip}`;
    }
    if (city) {
      readableAddress += `${readableAddress ? ' ' : ''}${city}`;
    }
    return readableAddress;
  }

  #setResidence(residence: string) {
    this.#residence.next(residence ?? '');
  }
}
